/* eslint-disable react-refresh/only-export-components -- deliberately a mixed module: shared tokens/helpers exported beside components; HMR full-reload is acceptable here. */
import { ReparoApi } from '@api';
import type {
    OrderPaymentSchedule,
    OrderPaymentTermsInput,
    PaymentRoute,
} from '@api/mother-order';
import type { VendorPaymentTermsConfig, VendorTermsOption } from '@api/vendors';
import {
    isSameTermsTemplate,
    listVendorTermsOptions,
    termsOptionSizeShortfall,
    termsOptionsForCountry,
    termsOptionsForOrderSize,
} from '@diagon-alley/common';
import { Checkbox, Group, Select, Stack, Text, TextInput } from '@mantine/core';
import { formatCount } from '@utils/currency';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';

import { formatMoney, textColors } from '../procurementOrders/shared';
import { TermsRows } from './TermsRows';
import {
    draftsFromTemplate,
    draftsSum,
    templateFromDrafts,
    type TrancheDraft,
} from './termsRows.model';

// ─── Draft model ────────────────────────────────────────────────

export type PaymentTermsDraft = {
    route: PaymentRoute;
    creditPartnerId: string | null;
    tranches: TrancheDraft[];
    /** Which of the vendor's named terms the rows came from; null when typed by hand. */
    termsOptionId: string | null;
    saveToVendorDefault: boolean;
    /** The name to keep the terms under on the vendor. */
    saveAsOptionName: string;
    /** The vendor offers more than one set of terms here, so one has to be picked. */
    choiceRequired: boolean;
    /** true once the user edits — stops vendor-default refills. */
    touched: boolean;
};

export type CreditPartnerOption = {
    vendorId: string;
    name: string;
    creditDays?: number;
    premiumPct?: number;
};

export const emptyPaymentTermsDraft = (): PaymentTermsDraft => ({
    route: 'DIRECT',
    creditPartnerId: null,
    tranches: [],
    termsOptionId: null,
    saveToVendorDefault: false,
    saveAsOptionName: '',
    choiceRequired: false,
    touched: false,
});

export const draftFromSchedule = (
    schedule: OrderPaymentSchedule | null,
): PaymentTermsDraft => ({
    ...emptyPaymentTermsDraft(),
    route: schedule?.route ?? 'DIRECT',
    creditPartnerId: schedule?.creditPartnerId ?? null,
    tranches: draftsFromTemplate(schedule?.template),
    termsOptionId: schedule?.termsOptionId ?? null,
});

/** Draft → API payload; undefined when no terms are entered (no schedule). */
export const paymentTermsToPayload = (
    draft: PaymentTermsDraft,
): OrderPaymentTermsInput | undefined => {
    const template = templateFromDrafts(draft.tranches);
    if (template.length === 0) return undefined;
    return {
        route: draft.route,
        creditPartnerId:
            draft.route === 'CREDIT_PARTNER' ? draft.creditPartnerId : null,
        template,
        termsOptionId: draft.termsOptionId ?? undefined,
        saveToVendorDefault: draft.saveToVendorDefault || undefined,
        saveAsOptionName: draft.saveToVendorDefault
            ? draft.saveAsOptionName.trim() || undefined
            : undefined,
    };
};

export const templateSum = (draft: PaymentTermsDraft): number => draftsSum(draft.tranches);

/** How an option reads in a picker: its name, whether it is the default, where it applies. */
export const termsOptionLabel = (option: VendorTermsOption): string =>
    [
        option.name,
        option.isDefault ? '(default)' : '',
        option.country ? `· ${option.country} only` : '',
    ]
        .filter(Boolean)
        .join(' ');

const ANCHOR_PHRASE: Record<string, string> = {
    ORDER_DATE: 'order date',
    READINESS: 'goods ready',
    DISPATCH: 'dispatch',
    ARRIVAL: 'arrival at port',
    DELIVERY: 'delivery',
    FIXED: 'a fixed date',
};

/** One line of what a set of terms says, for places too small to show its rows. */
export const termsSummary = (option: Pick<VendorTermsOption, 'tranches'>): string =>
    option.tranches
        .map((t) => {
            const days = t.offsetDays ?? 0;
            const anchor = ANCHOR_PHRASE[t.anchor] ?? t.anchor;
            const when =
                days === 0
                    ? `at ${anchor}`
                    : `${Math.abs(days)} days ${days > 0 ? 'after' : 'before'} ${anchor}`;
            return `${t.percentage}% ${(t.label || t.type).toLowerCase()} ${when}`;
        })
        .join(' · ');

type VendorConfigResponse = {
    name?: string;
    paymentTermsConfig?: VendorPaymentTermsConfig | null;
};

/** A vendor's saved terms, as the options an order can be placed on. */
export const fetchVendorTermsOptions = async (
    vendorId: string,
): Promise<VendorTermsOption[]> => {
    const vendor = await ReparoApi<VendorConfigResponse>({
        method: 'GET',
        url: `/vendors/${encodeURIComponent(vendorId)}`,
    });
    return listVendorTermsOptions(vendor?.paymentTermsConfig);
};

// ─── The card ───────────────────────────────────────────────────

/**
 * Payment terms editor for an order: route (direct / credit partner), which of the vendor's
 * terms the order is placed on, and the rows themselves with a live 100% indicator.
 *
 * A vendor with one set of terms has it applied without being asked. A vendor with more is
 * never guessed at: nothing is filled in until one is picked.
 */
export const PaymentTermsCard: FC<{
    vendorId: string | null;
    vendorName?: string;
    destinationCountry: string;
    orderTotal: number;
    /** Units on the order (Σ line quantities): a set of terms may carry a minimum in units. */
    orderQuantity?: number;
    currency: string;
    draft: PaymentTermsDraft;
    onChange: (draft: PaymentTermsDraft) => void;
}> = ({
    vendorId,
    vendorName,
    destinationCountry,
    orderTotal,
    orderQuantity,
    currency,
    draft,
    onChange,
}) => {
    const [partners, setPartners] = useState<CreditPartnerOption[]>([]);
    // Kept with the vendor they belong to: between picking a vendor and its terms arriving,
    // the previous vendor's options must not be offered - or applied - to the new one.
    const [loaded, setLoaded] = useState<{
        vendorId: string;
        options: VendorTermsOption[];
    } | null>(null);
    const draftRef = useRef(draft);
    draftRef.current = draft;

    // Credit partners = vendors with role 'Credit partner' (their financing
    // config rides the detail fetch below when one is chosen).
    useEffect(() => {
        let alive = true;
        ReparoApi<Array<{ vendorId: string; name: string; vendorRole?: string | null }>>({
            method: 'GET',
            url: '/vendors',
        })
            .then((rows) => {
                if (!alive) return;
                setPartners(
                    (rows ?? [])
                        .filter((v) => v.vendorRole === 'Credit partner')
                        .map((v) => ({ vendorId: v.vendorId, name: v.name })),
                );
            })
            .catch(() => undefined);
        return () => {
            alive = false;
        };
    }, []);

    useEffect(() => {
        if (!vendorId) return;
        let alive = true;
        fetchVendorTermsOptions(vendorId)
            .then((options) => {
                if (alive) setLoaded({ vendorId, options });
            })
            .catch(() => {
                if (alive) setLoaded({ vendorId, options: [] });
            });
        return () => {
            alive = false;
        };
    }, [vendorId]);

    const vendorLoaded = !!vendorId && loaded?.vendorId === vendorId;
    const options = useMemo(
        () => (vendorLoaded ? (loaded?.options ?? []) : []),
        [vendorLoaded, loaded],
    );
    const byCountry = useMemo(
        () => termsOptionsForCountry(options, destinationCountry),
        [options, destinationCountry],
    );
    // A set with a minimum this order does not reach — in value or in units — is shown
    // greyed, never applied: the minimum is the vendor's condition. Only the eligible sets
    // can apply on their own.
    const orderSize = useMemo(
        () => ({ value: orderTotal, quantity: orderQuantity ?? 0 }),
        [orderTotal, orderQuantity],
    );
    const offered = useMemo(
        () => termsOptionsForOrderSize(byCountry, orderSize),
        [byCountry, orderSize],
    );

    // Bring the draft in line with what this vendor offers for this destination and size.
    useEffect(() => {
        if (!vendorLoaded) return;
        const current = draftRef.current;
        let next = current;
        const choiceRequired = offered.length >= 2;
        if (current.choiceRequired !== choiceRequired) {
            next = { ...next, choiceRequired };
        }
        const pickedHere = current.touched && current.termsOptionId;
        if (pickedHere && !offered.some((o) => o.id === current.termsOptionId)) {
            // Picked for another destination: a UAE-only deal must not ride an India order.
            next = { ...next, termsOptionId: null, tranches: [] };
        } else if (
            current.tranches.length === 0 &&
            !current.termsOptionId &&
            offered.length === 1
        ) {
            next = {
                ...next,
                termsOptionId: offered[0].id,
                tranches: draftsFromTemplate(offered[0].tranches),
            };
        }
        if (next !== current) onChange(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reacts to what is offered, not to each keystroke in the draft
    }, [vendorLoaded, offered]);

    const touch = (patch: Partial<PaymentTermsDraft>): void =>
        onChange({ ...draft, ...patch, touched: true });

    const partner = partners.find((p) => p.vendorId === draft.creditPartnerId);
    const hasRows = draft.tranches.length > 0;
    const picked = options.find((o) => o.id === draft.termsOptionId);
    const edited =
        !!picked && !isSameTermsTemplate(picked.tranches, templateFromDrafts(draft.tranches));
    const canSaveToVendor = hasRows && !!vendorId && (!picked || edited);
    const vendorLabel = vendorName || 'this vendor';

    return (
        <Stack gap={8}>
            <Group gap={8} wrap="nowrap" align="flex-start" grow>
                <Stack gap={2}>
                    <Select
                        size="xs"
                        label="Pay via"
                        data={[
                            { value: 'DIRECT', label: 'Direct to vendor' },
                            ...partners.map((p) => ({
                                value: `partner:${p.vendorId}`,
                                label: `Credit partner — ${p.name}`,
                            })),
                        ]}
                        value={
                            draft.route === 'CREDIT_PARTNER' && draft.creditPartnerId
                                ? `partner:${draft.creditPartnerId}`
                                : 'DIRECT'
                        }
                        onChange={(v) => {
                            if (!v || v === 'DIRECT') {
                                touch({ route: 'DIRECT', creditPartnerId: null });
                            } else {
                                touch({
                                    route: 'CREDIT_PARTNER',
                                    creditPartnerId: v.replace('partner:', ''),
                                });
                            }
                        }}
                        allowDeselect={false}
                    />
                    {partners.length === 0 && (
                        <Text size="10px" c={textColors.muted}>
                            Financed routes appear here once a credit partner
                            exists (a vendor with role “Credit partner”).
                        </Text>
                    )}
                </Stack>
                {byCountry.length >= 2 && (
                    <Select
                        size="xs"
                        label={`Payment terms · ${vendorLabel} offers ${offered.length}${byCountry.length > offered.length ? ` of ${byCountry.length} at this order size` : ''}`}
                        placeholder={offered.length === 0 ? 'No terms at this order size' : 'Pick payment terms'}
                        withAsterisk
                        data={byCountry.map((o) => {
                            const short = termsOptionSizeShortfall(o, orderSize);
                            const needs =
                                short?.kind === 'quantity'
                                    ? `needs ≥ ${formatCount(short.needed)} units`
                                    : short
                                      ? `needs ≥ ${formatMoney(short.needed, currency)}`
                                      : null;
                            return {
                                value: o.id,
                                label: needs ? `${termsOptionLabel(o)} · ${needs}` : termsOptionLabel(o),
                                disabled: !!short,
                            };
                        })}
                        value={picked ? draft.termsOptionId : null}
                        error={
                            !hasRows ? 'Nothing is applied until you pick.' : undefined
                        }
                        onChange={(v) => {
                            const option = offered.find((o) => o.id === v);
                            if (!option) return;
                            touch({
                                termsOptionId: option.id,
                                tranches: draftsFromTemplate(option.tranches),
                                saveToVendorDefault: false,
                            });
                        }}
                        allowDeselect={false}
                    />
                )}
            </Group>
            {draft.route === 'CREDIT_PARTNER' && (
                <Text size="11px" c={textColors.muted}>
                    Non-advance payments route via {partner?.name ?? 'the partner'}{' '}
                    on credit (premium and credit days applied per their terms
                    when shipments are created). The advance is always paid to
                    the vendor.
                </Text>
            )}

            {picked && (
                <Text size="11px" c={textColors.muted}>
                    {edited
                        ? `Edited for this order — no longer the same as “${picked.name}”.`
                        : offered.length >= 2
                          ? `On “${picked.name}”.`
                          : `On “${picked.name}”, the only terms saved for ${vendorLabel}.`}
                </Text>
            )}

            {!hasRows && offered.length < 2 && (
                <Text size="12px" c={textColors.muted}>
                    {vendorId
                        ? `No payment terms saved for ${vendorLabel} — add them to enable payment tracking.`
                        : 'Pick a vendor, or add payment terms manually.'}
                </Text>
            )}

            <TermsRows
                tranches={draft.tranches}
                onChange={(tranches) => touch({ tranches })}
                amountFor={(t) =>
                    orderTotal > 0
                        ? formatMoney((orderTotal * (Number(t.percentage) || 0)) / 100, currency)
                        : '—'
                }
            />

            {canSaveToVendor && (
                <Group gap={8} align="center">
                    <Checkbox
                        size="xs"
                        label={`Save on ${vendorLabel} as new terms`}
                        checked={draft.saveToVendorDefault}
                        onChange={(e) =>
                            touch({
                                saveToVendorDefault: e.currentTarget.checked,
                                saveAsOptionName:
                                    draft.saveAsOptionName ||
                                    (options.length === 0 ? 'Standard' : ''),
                            })
                        }
                    />
                    {draft.saveToVendorDefault && (
                        <TextInput
                            size="xs"
                            w={220}
                            aria-label="Name for these terms"
                            placeholder="Name, e.g. 60-day credit"
                            value={draft.saveAsOptionName}
                            onChange={(e) =>
                                touch({ saveAsOptionName: e.currentTarget.value })
                            }
                        />
                    )}
                </Group>
            )}
        </Stack>
    );
};
